import { memo, useMemo } from 'react';
import { useTranslation } from '@betty/i18n';
import { formatAmount } from '@betty/toolkit/utils';
import { Box, Text } from '@betty/moonlight';
import { AmountsProps } from './Amounts.types';
import {
  groupByRows,
  getAmountLimit,
  checkIfOtherSelected,
  isAmountLimitReached,
  getLimitRemainingAmount,
} from './Amounts.utils';
import OtherAmount from './OtherAmount/OtherAmount';
import Amount from './Amount/Amount';
import AmountLimit from './AmountLimit/AmountLimit';

function Amounts({
  limits,
  selectedAmount,
  availableAmounts,
  onSelectAmount,
  onSelectOtherAmount,
}: AmountsProps) {
  const { t } = useTranslation('wallet');
  const rows = useMemo(() => groupByRows(availableAmounts), [availableAmounts]);
  const limit = useMemo(() => getAmountLimit(limits), [limits]);
  const otherSelected = useMemo(
    () => checkIfOtherSelected(availableAmounts, selectedAmount),
    [availableAmounts, selectedAmount],
  );
  const limitRemaining = getLimitRemainingAmount(limit);
  const limitReached = isAmountLimitReached(limit);

  return (
    <Box gap="space-600">
      <Text
        variant="body-m"
        locNS="wallet"
        locKey="depositAmountTitle"
        color="darkSurfacePrimary"
      />
      <Box gap="space-300">
        {rows.map((amounts, index) => (
          <Box key={index} flexDirection="row" gap="space-300">
            {amounts.map(({ amount }) => (
              <Amount
                key={amount}
                amount={amount}
                disabled={
                  limitRemaining !== undefined && limitRemaining < amount
                }
                selected={amount === selectedAmount && !limitReached}
                onPress={onSelectAmount}
              />
            ))}
            {index === rows.length - 1 && (
              <OtherAmount
                amount={selectedAmount}
                disabled={limitReached}
                selected={otherSelected}
                onPress={onSelectOtherAmount}
              />
            )}
          </Box>
        ))}
      </Box>
      {limitReached && (
        <Text variant="caption" color="darkSurfaceError">
          {t('depositLimitExceededForPeriod', {
            amount: formatAmount(limit?.amount ?? 0),
            period: limit?.period,
          })}
        </Text>
      )}
      {limit && <AmountLimit {...limit} />}
    </Box>
  );
}

export default memo(Amounts);
